/**
 * Returns the midpoint of the visible viewport area in canvas coordinates.
 * @param zoomLevel - Current zoom level of the whiteboard
 *
 * After zoom/fit-to-page/recenter, the CSS transform updates immediately
 * (same render) but scroll positions are adjusted in a useEffect (next
 * render). If this function runs between those two steps, scrollTop/Left
 * are stale relative to the new zoom. To compensate, we read the ACTUAL
 * rendered zoom from the canvas element's transform rather than trusting
 * the passed-in zoomLevel alone, and we verify scroll position consistency.
 */
export var getVisibleMidpoint = function (zoomLevel) {
    var el = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
    var y = 0, x = 0;
    if (el) {
        // Read the actual applied zoom from the CSS transform on .mainHolder,
        // which is always in sync with the current render (no effect delay).
        var z = zoomLevel || 1;
        var mainHolder = el.querySelector('.mainHolder');
        if (mainHolder) {
            var transform = getComputedStyle(mainHolder).transform;
            if (transform && transform !== 'none') {
                // transform is "matrix(a, b, c, d, tx, ty)" — a = scaleX
                var match = transform.match(/^matrix\(([^,]+)/);
                if (match) {
                    var parsed = parseFloat(match[1]);
                    if (parsed > 0)
                        z = parsed;
                }
            }
        }
        var top_1 = el.scrollTop / z;
        y = top_1 + el.clientHeight / z / 2;
        var left = el.scrollLeft / z;
        x = left + el.clientWidth / z / 2;
    }
    return { x: x, y: y };
};
/**
 * Derive an axis-aligned bounding box for a manipulative data blob. Falls
 * back to `radius * 2` when `dimensions` is missing (e.g. spinners). Returns
 * null for items without a usable box.
 */
function getManipulativeRect(data) {
    var _a, _b, _c, _d, _e;
    if (!(data === null || data === void 0 ? void 0 : data.center))
        return null;
    var scale = (_a = data.scale) !== null && _a !== void 0 ? _a : 1;
    var w = ((_c = (_b = data.dimensions) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : (data.radius ? data.radius * 2 : 0)) * scale;
    var h = ((_e = (_d = data.dimensions) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : (data.radius ? data.radius * 2 : 0)) * scale;
    if (!w || !h)
        return null;
    return {
        x1: data.center.x - w / 2,
        y1: data.center.y - h / 2,
        x2: data.center.x + w / 2,
        y2: data.center.y + h / 2,
    };
}
/**
 * Starting from `initial`, walk an expanding 8-directional spiral until a
 * spot is found where the given box does not intersect any existing
 * manipulative's bounding rect. If 20 rings yield nothing, the initial point
 * is returned anyway — better to overlap than to silently drop the drop.
 */
export var findFreeDropPosition = function (initial, size, manipulative, padding) {
    if (padding === void 0) { padding = 8; }
    var boxes = manipulative
        .map(function (m) { return getManipulativeRect(m.data); })
        .filter(function (r) { return !!r; });
    var overlaps = function (cx, cy) {
        var x1 = cx - size.width / 2 - padding;
        var y1 = cy - size.height / 2 - padding;
        var x2 = cx + size.width / 2 + padding;
        var y2 = cy + size.height / 2 + padding;
        return boxes.some(function (r) { return !(x2 <= r.x1 || x1 >= r.x2 || y2 <= r.y1 || y1 >= r.y2); });
    };
    if (!overlaps(initial.x, initial.y))
        return initial;
    var step = Math.max(size.width, size.height) + padding * 2;
    var directions = [
        [1, 0],
        [1, 1],
        [0, 1],
        [-1, 1],
        [-1, 0],
        [-1, -1],
        [0, -1],
        [1, -1],
    ];
    for (var ring = 1; ring <= 20; ring++) {
        for (var _i = 0, directions_1 = directions; _i < directions_1.length; _i++) {
            var _a = directions_1[_i], dx = _a[0], dy = _a[1];
            var nx = initial.x + dx * step * ring;
            var ny = initial.y + dy * step * ring;
            if (!overlaps(nx, ny))
                return { x: nx, y: ny };
        }
    }
    return initial;
};
